/**
 * navModel - pure description of the main menu (CRM-178, Option B).
 *
 * Turns the user's roles, feature flags and loaded counts into the items the top bar,
 * the profile menu and the hamburger menu render. Keeping the role rules here (and only
 * here) means the desktop bar and the mobile menu cannot drift apart.
 */

import type { UserRole } from '../../types';

export interface NavLinkItem {
  key: string;
  label: string;
  to: string;
  count?: number;
  external?: boolean;
}

export interface NavGroup {
  key: 'manage' | 'tools';
  label: string;
  items: NavLinkItem[];
  count: number;
}

export type TopEntry = { kind: 'link'; item: NavLinkItem } | { kind: 'group'; group: NavGroup };

export interface NavModel {
  /** Desktop row, in order. */
  top: TopEntry[];
  /** Admin section of the profile menu (may be empty). */
  profileAdmin: NavLinkItem[];
  /** Hamburger sections; empty sections are omitted. */
  mobileSections: { heading: string; items: NavLinkItem[] }[];
}

export interface NavInput {
  role: string | undefined;
  hasAnyRole: (...roles: UserRole[]) => boolean;
  isAdmin: boolean;
  flags: { callQueues: boolean; campaigns: boolean; devLogin: boolean };
  /** coachFlags = queueCount + callListCount */
  counts: { myOpen: number; pending: number; leads: number; coachFlags: number };
}

/** Group with 0 items is dropped; exactly 1 item becomes a plain link that keeps its own count. */
function groupEntry(
  key: NavGroup['key'],
  label: string,
  items: NavLinkItem[],
): TopEntry | null {
  if (items.length === 0) return null;
  if (items.length === 1) return { kind: 'link', item: items[0] };
  const count = items.reduce((sum, i) => sum + (i.count ?? 0), 0);
  return { kind: 'group', group: { key, label, items, count } };
}

export function buildNavModel(i: NavInput): NavModel {
  const { role, hasAnyRole, isAdmin, flags, counts } = i;

  const workLinks: NavLinkItem[] = [
    { key: 'dashboard', label: 'Dashboard', to: '/' },
    { key: 'people', label: 'People', to: '/people' },
    { key: 'organizations', label: 'Organizations', to: '/organizations' },
    { key: 'lists', label: 'Lists', to: '/lists' },
    { key: 'activities', label: 'Activities', to: '/activities' },
  ];
  if (role === 'SALES_REP' && !hasAnyRole('MANAGER')) {
    workLinks.push({ key: 'my-requests', label: 'My requests', to: '/requests', count: counts.myOpen });
  }

  const manageItems: NavLinkItem[] = [];
  if (hasAnyRole('MANAGER')) {
    manageItems.push({ key: 'team', label: 'Team', to: '/team' });
    manageItems.push({ key: 'requests', label: 'Requests', to: '/requests', count: counts.pending });
    manageItems.push({ key: 'leads', label: 'Leads', to: '/leads', count: counts.leads });
  }
  if (hasAnyRole('DATA_STEWARD', 'MANAGER', 'INSIDE_SALES')) {
    manageItems.push({ key: 'coach-flags', label: 'Coach flags', to: '/coach-flags', count: counts.coachFlags });
  }
  if (flags.callQueues && hasAnyRole('INSIDE_SALES', 'MANAGER')) {
    manageItems.push({ key: 'call-queues', label: 'Call queues', to: '/call-queues' });
  }

  const toolsItems: NavLinkItem[] = [
    { key: 'explore', label: 'Explore', to: '/explore' },
    { key: 'competitors', label: 'Competitors', to: '/competitors' },
  ];
  if (flags.campaigns && hasAnyRole('CAMPAIGN_MANAGER')) {
    toolsItems.push({ key: 'campaigns', label: 'Campaigns', to: '/campaigns' });
  }

  const profileAdmin: NavLinkItem[] = [];
  if (hasAnyRole('ADMIN', 'MATCHING_SUPPORT')) {
    profileAdmin.push({ key: 'admin', label: 'Admin', to: '/admin' });
  }
  if (isAdmin) {
    profileAdmin.push({ key: 'territories', label: 'Territories', to: '/territories' });
  }
  if (flags.devLogin && isAdmin) {
    profileAdmin.push({ key: 'dev-login', label: 'Dev Login', to: '/dev/login' });
  }

  const top: TopEntry[] = workLinks.map((item) => ({ kind: 'link', item }));
  // Tools is a dropdown only alongside a Manage dropdown. Without one (no manage items, or the
  // one-item rule collapsed it to a plain link) the bar has room, so Tools items sit top-level.
  const manage = groupEntry('manage', 'Manage', manageItems);
  if (manage) top.push(manage);
  if (!manage || manage.kind === 'link') {
    toolsItems.forEach((item) => top.push({ kind: 'link', item }));
  } else {
    const tools = groupEntry('tools', 'Tools', toolsItems);
    if (tools) top.push(tools);
  }

  // Mobile ignores the one-item rule: its sections are just headings over flat lists.
  const sections: { heading: string; items: NavLinkItem[] }[] = [
    { heading: 'Work', items: workLinks },
    { heading: 'Manage', items: manageItems },
    { heading: 'Tools', items: toolsItems },
    {
      heading: 'Account',
      items: [
        { key: 'settings', label: 'Settings', to: '/settings' },
        { key: 'help', label: 'Help', to: '/help/', external: true },
        ...profileAdmin,
      ],
    },
  ];

  return { top, profileAdmin, mobileSections: sections.filter((s) => s.items.length > 0) };
}
